import { Typography } from '@euler/app/components/typography';
import { InspectionResultFormatter } from '@euler/app/flows/report/functions';
import { SiteIcon } from '@euler/app/flows/task/components/SiteIcon';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { DefectiveBadge } from '@euler/app/tv/components/DefectiveBadge';
import { EmptyPlaceholder } from '@euler/app/tv/components/EmptyPlaceholder';
import {
  InspectedItem,
  SiteInspectionCard,
} from '@euler/app/tv/components/inspection/types';
import { useDefectiveBadgeColorMap } from '@euler/app/tv/functions/useDefectiveBadgeColorMap';
import {
  SiteInspectionCardFilter,
  useSiteInspectionCards,
} from '@euler/app/tv/functions/useSiteInspectionCards';
import { useScreenContentPadding } from '@euler/app/tv/functions/utils';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { PresentationTheme } from '@euler/app/tv/theme/types';
import { Center } from '@euler/components/Center';
import { HStack } from '@euler/components/layout/Stack';
import { getDefectiveLevel } from '@euler/functions/getDefectiveLevel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  DefectiveLevel,
  DefectiveLevelColorMap,
  InspectionSiteInfo,
} from '@euler/model';
import { VehicleSystemModule } from '@euler/model/entity';
import { comparerDesc, delayed } from '@euler/utils';
import { sorted } from '@euler/utils/array';
import { usePrevious } from '@euler/utils/hooks';
import { MotiView } from 'moti';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  FlatList,
  ListRenderItem,
  Platform,
  ScrollView,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export const WiredModulePage = memo(
  ({
    width,
    active,
    onCardPress,
    ...props
  }: {
    mod?: VehicleSystemModule;
    modId: number;
    modName: string;
    width: number;
    active?: boolean;
    style?: StyleProp<ViewStyle>;
    onCardPress?: (card: SiteInspectionCard) => void;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const colorMap = useDefectiveBadgeColorMap();
    const { bottom } = useSafeAreaInsets();
    const { detail, inventoryQuery } = useTaskContext();
    const [paddingLeft, paddingRight] = useScreenContentPadding();
    const [visible, setVisible] = useState(active);
    const previousActive = usePrevious(active);

    const filter = useMemo<SiteInspectionCardFilter>(
      () => ({ type: 'system-module', currentSystemModId: props.modId }),
      [props.modId],
    );
    const cards = useSiteInspectionCards(detail, inventoryQuery, filter);

    const itemHeight = rpx(668) - bottom;
    const itemWidth = itemHeight * (528 / 668);

    useEffect(() => {
      if (!active) {
        if (previousActive) {
          return delayed(() => {
            setVisible(false);
          }, 350);
        }
      } else {
        setVisible(true);
      }
    }, [active, previousActive, visible]);

    const renderItem: ListRenderItem<SiteInspectionCard> = useCallback(
      ({ item, index }) => {
        return (
          <Cell
            card={item}
            index={index}
            theme={theme}
            colorMap={colorMap}
            iconSize={rpx(348) - bottom}
            onPress={onCardPress}
            css={`
              width: ${itemWidth}px;
              height: ${itemHeight}px;
              margin-right: ${rpx(40)}px;
            `}
          />
        );
      },
      [bottom, colorMap, itemHeight, itemWidth, onCardPress, rpx, theme],
    );

    return (
      <View
        css={`
          flex-grow: 0;
          flex-shrink: 0;
          width: ${width}px;
          justify-content: center;
          flex-direction: row;
          top: ${-(bottom / 2)}px;
        `}
        style={props.style}
      >
        {visible ? (
          cards.length > 0 ? (
            <FlatList
              data={cards}
              renderItem={renderItem}
              keyExtractor={x => x.key}
              horizontal
              showsHorizontalScrollIndicator={Platform.OS === 'web'}
              contentContainerStyle={{
                paddingLeft,
                paddingRight,
                alignItems: 'center',
                justifyContent: 'center',
              }}
              css={`
                width: 100%;
              `}
            />
          ) : (
            <EmptyPlaceholder
              text="暂无相关检测结果"
              iconSize={rpx(200)}
              textStyle={{
                fontSize: rpx(36),
                marginTop: rpx(40),
              }}
            />
          )
        ) : null}
      </View>
    );
  },
);

const Cell = memo(
  ({
    card,
    index,
    style,
    theme,
    colorMap,
    iconSize,
    onPress,
  }: {
    card: SiteInspectionCard;
    index: number;
    style?: StyleProp<ViewStyle>;
    theme: PresentationTheme;
    colorMap: DefectiveLevelColorMap;
    iconSize: number;
    onPress?: (card: SiteInspectionCard) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const site = card.type === 'inspected-site' ? card.site : undefined;
    const icon = site?.iconUrl;
    const type = icon ? (icon.includes('.svg') ? 'svg' : 'image') : undefined;

    const items = card.items;

    const defectiveLevel =
      items.length > 0
        ? getDefectiveLevel(items[0].abnormalLevel, items[0].severityLevel)
        : DefectiveLevel.Fine;

    const handlePress = useCallback(() => {
      onPress?.(card);
    }, [card, onPress]);

    return (
      <MotiView
        from={{ opacity: 0, translateX: 150 }}
        animate={{ opacity: 1, translateX: 0 }}
        transition={{ type: 'timing', duration: 300 }}
        delay={Math.min(1500, index * 50) + 150}
        css={`
          border-radius: ${rpx(20)}px;
          overflow: hidden;
          background-color: ${theme.task.inspection.siteCard.bg};
        `}
        style={style}
      >
        <TouchableOpacity
          css={`
            flex: 1;
          `}
          onPress={handlePress}
        >
          {/* {site?.imgUrl != null && (
            <AdvancedImage
              uri={site.imgUrl}
              style={StyleSheet.absoluteFill}
              resizeMode="cover"
            />
          )} */}
          <View
            css={`
              margin-top: ${rpx(40)}px;
              margin-left: ${rpx(40)}px;
              flex-direction: row;
              justify-content: space-between;
              align-items: center;
            `}
          >
            <Typography.Label
              medium
              color={theme.task.inspection.siteCard.titleTextColor}
              size={rpx(36)}
              numberOfLines={1}
              css={`
                flex: 1;
              `}
            >
              {`${card.name}`}
            </Typography.Label>
            <DefectiveBadge
              defectiveLevel={defectiveLevel}
              colorMap={colorMap}
              css={`
                margin-left: ${rpx(15)}px;
              `}
            />
          </View>
          <Center
            css={`
              flex: 1;
              align-self: center;
            `}
          >
            <SiteIcon
              url={icon}
              type={type}
              size={iconSize}
              color={theme.task.inspection.siteCard.siteIconColor}
            />
          </Center>
          <View
            css={`
              margin-left: ${rpx(40)}px;
              margin-right: ${rpx(40)}px;
              margin-bottom: ${rpx(40)}px;
              background-color: ${theme.task.inspection.siteCard
                .separatorColor};
              height: 1px;
              flex-shrink: 0;
            `}
          />
          <View
            onStartShouldSetResponder={() => true}
            css={`
              margin-bottom: ${rpx(32)}px;
            `}
          >
            <ScrollView
              css={`
                flex-grow: 0;
                height: ${rpx(48) * 3 - rpx(12)}px;
              `}
              contentContainerStyle={{
                justifyContent: 'center',
                paddingLeft: rpx(40),
                paddingRight: rpx(40),
              }}
              showsVerticalScrollIndicator={false}
            >
              {sorted(items, (x, y) =>
                comparerDesc(x.severityLevel, y.severityLevel),
              ).map((item, i) => (
                <ItemCell
                  key={item.key}
                  site={site}
                  inspectedItem={item}
                  theme={theme}
                  colorMap={colorMap}
                  last={i === card.items.length - 1}
                />
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </MotiView>
    );
  },
);

const ItemCell = memo(
  ({
    inspectedItem,
    style,
    colorMap,
    theme,
    last,
  }: {
    site?: InspectionSiteInfo;
    inspectedItem: InspectedItem;
    style?: StyleProp<ViewStyle>;
    theme: PresentationTheme;
    colorMap: DefectiveLevelColorMap;
    last?: boolean;
  }) => {
    const { rpx } = useResponsiveContext();

    const defectiveLevel = getDefectiveLevel(
      inspectedItem.abnormalLevel,
      inspectedItem.severityLevel,
    );
    const color = colorMap[defectiveLevel];

    const name =
      InspectionResultFormatter.getInspectedItemDisplayName(inspectedItem);

    const [label, valueRange] =
      InspectionResultFormatter.formatLabel(inspectedItem);

    return (
      <HStack
        justifyContent="flex-start"
        css={`
          height: ${rpx(36)}px;
          margin-bottom: ${last ? 0 : rpx(12)}px;
        `}
        style={style}
      >
        <View
          css={`
            width: ${rpx(6)}px;
            height: ${rpx(6)}px;
            border-radius: ${rpx(6) / 2}px;
            background-color: ${color};
            margin-right: ${rpx(8)}px;
          `}
        />
        <HStack
          css={`
            flex: 1;
          `}
        >
          <Typography.Label
            light
            size={rpx(24)}
            lineHeight={rpx(36)}
            numberOfLines={1}
            css={`
              color: ${theme.task.inspection.siteCard.labelTextColor};
              flex-grow: 1;
              margin-right: ${rpx(10)}px;
            `}
          >{`${name}: `}</Typography.Label>
          <Typography.Label
            regular
            size={rpx(24)}
            color={theme.task.inspection.siteCard.valueTextColor}
            lineHeight={rpx(36)}
            numberOfLines={1}
            css={`
              flex-grow: 0;
              flex-shrink: 0;
            `}
          >
            {`${label}${valueRange != null ? ' [检测值]' : ''}`}
          </Typography.Label>
        </HStack>
      </HStack>
    );
  },
);
